// Temporary: ExperienceCompactSearchTile from PR #32112 (EXP-3482-3245) forced to always
// render in mobile layout. Replace with the real component once that PR is merged.
import BodyText from 'LuxKit/Typography/BodyText'
import { rem } from 'polished'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import styled from 'styled-components'
import Image from 'components/Common/Image'
import NestableClickable from 'components/Common/NestableClickable'
import TripGuard from 'tripPlanner/components/TripGuard/TripGuard'
import { ExperienceBookmarkButton } from 'tripPlanner/components/Bookmark/BookmarkButton'
import config from 'constants/config'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import { getImmersiveTripId } from 'tripPlanner/selectors'
import PriceRowAgentHubCommission from 'agentHub/components/PriceRowAgentHubCommission'
import { useInView } from 'react-intersection-observer'
import useTrackExperienceOfferEvent from 'contexts/support/useTrackExperienceOfferEvent'
import { useAppSelector } from 'hooks/reduxHooks'
import { EventDataKey } from 'home/pages/HomePage/useHomepageAnalytics'
import OfferListEventsContext, { OfferListEvents } from 'components/OfferList/OfferListEventsContext'
import { OFFER_TRACKING_IN_VIEW_THRESHOLD } from 'constants/offerList'
import PriceRowPriceWithCaption from 'LuxKit/PricePoints/PriceRowPriceWithCaption'
import { getAgentHubExperienceType } from 'lib/agentHub/agentHubUtils'
import { getExperienceOfferPageURL } from 'lib/offer/offerPageURL'
import { getExperienceCompactLocation } from 'lib/experiences/experienceUtils'

const IMAGE_SIZE = 96

const BadgeContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  inset-inline-end: ${rem(8)};
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: ${rem(2)};
  z-index: 1;
`

const Tile = styled(NestableClickable)`
  display: grid;
  grid-template-columns: ${rem(IMAGE_SIZE)} 1fr;
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  border-radius: ${props => props.theme.borderRadius.XS};
  overflow: hidden;
  min-height: ${rem(92)};
`

const ImageWrapper = styled.div`
  position: relative;
  overflow: hidden;
`

const TileContent = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: ${rem(12)} ${rem(12)};
  gap: ${rem(8)};
  min-width: 0;
`

interface Props {
  experience: App.ExperienceOffer
  position?: number
}

function ExperienceLoungeOffersBannerTile({ experience, position }: Props) {
  const intl = useIntl()
  const trackEvent = useTrackExperienceOfferEvent()
  const dispatchOfferListEvent = useContext(OfferListEventsContext)
  const [imageLoaded, setImageLoaded] = useState(false)

  const [inViewRef, inView] = useInView({
    triggerOnce: true,
    threshold: OFFER_TRACKING_IN_VIEW_THRESHOLD,
  })

  useEffect(() => {
    if (inView && imageLoaded && position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productImpression,
        offer: experience,
        position,
        key: EventDataKey.ProductImpression,
      })

      trackEvent('OFFER_TILE_IMPRESSION', [{
        type: 'impression',
        data: { offer: experience },
      }])
    }
  }, [inView, imageLoaded, position, dispatchOfferListEvent, trackEvent, experience])

  const handleImageLoaded = useCallback(() => setImageLoaded(true), [])

  const productType = useMemo(() => getAgentHubExperienceType(experience), [experience])

  const tripId = useAppSelector(getImmersiveTripId)
  const offerPageQueryParams = useMemo(() => {
    if (config.OPEN_NEW_TAB_OFFER_CLICK && tripId) return new URLSearchParams({ tripId })
    return undefined
  }, [tripId])

  const handleViewOffer = useCallback(() => {
    if (position !== undefined) {
      dispatchOfferListEvent({
        type: OfferListEvents.productClick,
        offer: experience,
        position,
        key: EventDataKey.ProductClick,
      })
    }

    trackEvent('OFFER_TILE_CLICKED', [{
      type: 'click',
      data: { offer: experience },
    }])
  }, [experience, dispatchOfferListEvent, position, trackEvent])

  const compactLocation = useMemo(
    () => getExperienceCompactLocation(experience.location, 40) ?? experience.location.name,
    [experience.location],
  )
  const defaultTicketUnitLabel = intl.formatMessage({
    id: 'P3gWPn5epwh5yZWRxHhU',
    defaultMessage: 'total',
    description: 'Default sale unit label for compact experience search tile pricing',
  })
  const fromCaption = intl.formatMessage({
    id: '1HqkAJtKjpKq56bGHEDj',
    defaultMessage: 'From',
    description: 'Price caption prefix on experience recently viewed tile',
  })
  const title = experience.name

  return (
    <Tile
      data-testid="experience-offer-tile"
      to={getExperienceOfferPageURL(experience, offerPageQueryParams)}
      target={config.OPEN_NEW_TAB_OFFER_CLICK ? '_blank' : undefined}
      rel={config.OPEN_NEW_TAB_OFFER_CLICK ? 'noopener noreferrer' : undefined}
      onClick={handleViewOffer}
      ref={inViewRef}
    >
      <ImageWrapper>
        {experience.images[0] && (
          <Image
            height={IMAGE_SIZE}
            width={IMAGE_SIZE}
            image={experience.images[0]}
            fit="center"
            onLoad={handleImageLoaded}
          />
        )}
        <BadgeContainer>
          <TripGuard>
            <ExperienceBookmarkButton offer={experience} withLabel="never" size="small" />
          </TripGuard>
        </BadgeContainer>
      </ImageWrapper>
      <TileContent>
        <VerticalSpacer gap={4}>
          {compactLocation && (
            <BodyText variant="small" colour="light-foreground-alt">{compactLocation}</BodyText>
          )}
          <BodyText title={title} lineClamp={2} variant="medium" weight="bold">{title}</BodyText>
        </VerticalSpacer>
        <div>
          {!!experience.price && (
            <PriceRowPriceWithCaption
              caption={fromCaption}
              price={experience.price}
              saleUnit={experience.ticketUnitLabel ?? defaultTicketUnitLabel}
              size="S"
            />
          )}
          <PriceRowAgentHubCommission
            offerId={experience.id}
            productType={productType}
            offerLocations={[experience.location.name]}
            offer={experience}
          />
        </div>
      </TileContent>
    </Tile>
  )
}

export default ExperienceLoungeOffersBannerTile
